/**
 * How the tenant directory is described, filtered, ordered and validated.
 *
 * Pure, so the master list's rules can be tested without a console around
 * them.
 *
 * @module components/platform/tenant-directory
 */

import type { CreateTenantInput, TenantOnboardingSummary } from "@/platform";
import { isReservedTenantSlug } from "@/server/tenant-addressing";
import type { FieldProblem } from "@/utils/field-problems";

export type OnboardingStatus = TenantOnboardingSummary["onboardingStatus"];

/** Human-readable label for each onboarding state. */
export const ONBOARDING_LABELS: Record<OnboardingStatus, string> = {
  "pending-identity": "Awaiting first sign-in",
  active: "Active",
  suspended: "Suspended",
};

/** Phosphor icon paired with each onboarding state in the master list. */
export const ONBOARDING_ICONS: Record<OnboardingStatus, string> = {
  "pending-identity": "clock-countdown",
  active: "check-circle",
  suspended: "pause-circle",
};

/** The one state a tenant shows, and the second only when the two fields disagree. */
export interface TenantState {
  /** A status token `StatusBadge` knows. */
  status: string;
  label: string;
  /** Set when the other field says something the primary label hides. */
  alsoLabel?: string;
}

/**
 * The single state to show for a tenant.
 *
 * `status` (active or deactivated) and `onboardingStatus` (pending-identity,
 * active or suspended) are independent fields. Deactivated wins; otherwise the
 * onboarding state when it is not active; otherwise Active. A deactivated
 * tenant that is also not onboarded keeps both, so nothing is hidden.
 */
export function tenantState(tenant: TenantOnboardingSummary): TenantState {
  const onboarding = tenant.onboardingStatus;
  if (tenant.status === "deactivated") {
    return {
      status: "deactivated",
      label: "Deactivated",
      alsoLabel: onboarding === "active" ? undefined : ONBOARDING_LABELS[onboarding],
    };
  }
  if (onboarding !== "active") {
    return { status: onboarding, label: ONBOARDING_LABELS[onboarding] };
  }
  return { status: "active", label: "Active" };
}

/** Sort orders offered above the tenant master list. */
export type TenantSort = "name" | "created-desc" | "status";

export const SORT_OPTIONS: readonly { value: TenantSort; label: string }[] = [
  { value: "name", label: "Name (A–Z)" },
  { value: "created-desc", label: "Newest first" },
  { value: "status", label: "Onboarding state" },
];

/** Rank onboarding states so the ones needing attention sort first. */
const ONBOARDING_RANK: Record<OnboardingStatus, number> = {
  "pending-identity": 0,
  suspended: 1,
  active: 2,
};

/**
 * Filter tenants by a free-text query across the fields shown in the row.
 *
 * Matching the visible text is deliberate: a Platform Admin types what they
 * can see, so the nominated administrator's address has to match even though
 * it only appears on tenants still awaiting onboarding.
 */
export function matchesTenant(tenant: TenantOnboardingSummary, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) {
    return true;
  }
  return [
    tenant.name,
    tenant.slug ?? "",
    tenant.status,
    ONBOARDING_LABELS[tenant.onboardingStatus],
    tenant.nominatedAdminEmail ?? "",
  ].some((value) => value.toLocaleLowerCase().includes(normalized));
}

export function sortTenants(
  tenants: readonly TenantOnboardingSummary[],
  order: TenantSort,
): TenantOnboardingSummary[] {
  const sorted = [...tenants];
  switch (order) {
    case "name":
      return sorted.sort((left, right) => left.name.localeCompare(right.name));
    case "created-desc":
      return sorted.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    case "status":
      return sorted.sort(
        (left, right) =>
          ONBOARDING_RANK[left.onboardingStatus] - ONBOARDING_RANK[right.onboardingStatus] ||
          left.name.localeCompare(right.name),
      );
    default: {
      // Unreachable: every member of the union has a case above, and this
      // assignment is what makes tsc say so if one is ever added without one.
      const unhandled: never = order;
      throw new Error(`Unhandled sort order: ${String(unhandled)}`);
    }
  }
}

/** The create form's fields a validator can refuse. */
export type TenantFormField = "name" | "slug" | "adminEmail";

/**
 * Validate the create form against the same rules the server applies.
 *
 * Duplicated deliberately rather than shared: this is a convenience so a
 * Platform Admin sees the problem before a round trip, and the server's copy
 * remains the one that decides. A caller who bypasses this form is still
 * refused.
 *
 * @param form - The current form values.
 * @returns Every problem, in the order the fields appear on screen.
 */
export function validateTenantForm(form: CreateTenantInput): FieldProblem<TenantFormField>[] {
  const problems: FieldProblem<TenantFormField>[] = [];
  if (!form.name.trim()) {
    problems.push({ field: "name", message: "An organisation name is required." });
  }
  const slug = form.slug.trim().toLowerCase();
  // Checked apart from the character rule, which an empty slug also fails:
  // somebody who typed nothing needs to hear the field is needed.
  if (!slug) {
    problems.push({ field: "slug", message: "A slug is required." });
  } else if (!/^[a-z0-9-]+$/.test(slug)) {
    problems.push({
      field: "slug",
      message: "A slug can contain lowercase letters, digits, and hyphens only.",
    });
  } else if (isReservedTenantSlug(slug)) {
    problems.push({
      field: "slug",
      message: `The slug ${slug} is reserved for the application. Choose another.`,
    });
  }
  if (!form.adminEmail.trim()) {
    problems.push({ field: "adminEmail", message: "An administrator email is required." });
  }
  return problems;
}
